import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Facebook, Instagram, Mail } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { axiosInstance } from "@/App";
import { startCognitoLogin } from "@/utils/cognitoAuth";

const PROVIDER_DETAILS = {
  google: { label: "Continue with Google" },
  facebook: {
    label: "Continue with Facebook",
    className: "bg-[#1877f2] text-white",
  },
  instagram: {
    label: "Continue with Instagram",
    className: "bg-gradient-to-br from-[#833ab4] via-[#e1306c] to-[#f77737] text-white",
  },
  twitter: { label: "Continue with X", className: "bg-white text-black" },
};

const SOCIAL_PROVIDERS = ["facebook", "instagram", "twitter"];

const ProviderMark = ({ provider }) => {
  if (provider === "facebook") return <Facebook className="h-7 w-7 fill-current" aria-hidden="true" />;
  if (provider === "instagram") return <Instagram className="h-7 w-7" strokeWidth={2.4} aria-hidden="true" />;
  if (provider === "twitter") return <span className="text-2xl font-black leading-none" aria-hidden="true">X</span>;
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [providers, setProviders] = useState({ google: true, email: false });
  const [email, setEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const loginError = new URLSearchParams(window.location.search).get("error");

  useEffect(() => {
    axiosInstance.get("/auth/social/providers", { timeout: 6000 })
      .then(({ data }) => setProviders(data))
      .catch(() => setProviders({ google: true, email: false }));
  }, []);

  const startLogin = (provider) => {
    if (providers[provider] !== true) return;
    setLoading(provider);
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backend}/api/auth/social/${provider}/login`;
  };

  const startEmailLogin = async () => {
    if (providers.cognito !== true) return;
    setLoading("cognito");
    try {
      await startCognitoLogin();
    } catch (error) {
      setLoading(null);
      window.location.assign("/?error=aws_email_unavailable");
    }
  };

  const requestEmailLink = async (event) => {
    event.preventDefault();
    if (!email.trim() || providers.email !== true) return;
    setLoading("email");
    setEmailError("");
    setEmailMessage("");
    try {
      const { data } = await axiosInstance.post("/auth/email/request-link", {
        email: email.trim(),
      });
      setEmailMessage(data?.message || "Check your email for your secure sign-in link.");
    } catch (error) {
      setEmailError(
        error?.response?.data?.detail
          || "We could not send the sign-in email. Please try again."
      );
    } finally {
      setLoading(null);
    }
  };

  const unavailableProviders = SOCIAL_PROVIDERS
    .filter((provider) => providers[provider] !== true)
    .map((provider) => provider === "twitter" ? "X" : provider[0].toUpperCase() + provider.slice(1));

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#080b12] px-4 pb-8 pt-20 sm:flex sm:items-center sm:justify-center sm:py-24" data-testid="login-page">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[520px]"
        style={{ background: "radial-gradient(ellipse at center, rgba(37,99,235,.28), transparent 68%)" }} />
      <div className="pointer-events-none absolute -bottom-52 -right-32 w-[560px] h-[560px]"
        style={{ background: "radial-gradient(circle, rgba(37,99,235,.14), transparent 70%)" }} />

      <div className="absolute left-0 right-0 top-0 flex h-20 items-center px-5 sm:px-8">
        <button onClick={() => navigate("/events")} className="flex items-center" data-testid="login-logo">
          <BrandLogo imageClassName="h-12 w-auto" />
        </button>
      </div>

      <div
        className="relative mx-auto w-full max-w-[448px] rounded-[26px] border border-white/10 px-5 py-8 sm:px-10 sm:py-10"
        style={{ background: "rgba(20,20,22,.72)", backdropFilter: "blur(18px)", boxShadow: "0 30px 80px rgba(0,0,0,.5)" }}
        data-testid="login-card"
      >
        <h1 className="mb-3 text-center text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Welcome to ShowMeLive</h1>
        <p className="mb-4 text-center text-sm font-semibold uppercase tracking-[0.2em] text-blue-400">No Fan Left Behind</p>
        <p className="mb-7 text-center text-[15px] leading-relaxed text-gray-400">
          Host and watch premium live events in one powerful experience.
        </p>

        {loginError && (
          <div
            className="mb-5 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-center text-sm text-red-200"
            role="alert"
          >
            Sign-in could not be completed ({loginError.replaceAll("_", " ")}). Please try again.
          </div>
        )}

        <div className="space-y-3">
          {providers.cognito === true && providers.email !== true && (
            <button
              type="button"
              onClick={startEmailLogin}
              disabled={Boolean(loading)}
              className="relative flex h-14 w-full items-center justify-center gap-3 rounded-2xl border border-blue-400/50 bg-blue-600 text-[16px] font-bold text-white transition-all hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-45"
              data-testid="login-cognito"
            >
              <span className="text-xl" aria-hidden="true">✉</span>
              {loading === "cognito" ? "Opening secure email sign-in..." : "Continue with Email"}
              <span className="absolute right-4 rounded-full bg-[#13213d] px-2 py-1 text-[9px] uppercase tracking-wider text-blue-200">AWS</span>
            </button>
          )}
          {["google"].map((provider) => {
            const detail = PROVIDER_DETAILS[provider];
            const isAvailable = providers[provider] === true;
            return (
              <button
                key={provider}
                onClick={() => startLogin(provider)}
                disabled={Boolean(loading) || !isAvailable}
                className={`relative flex h-14 w-full items-center justify-center gap-3 rounded-2xl border text-[16px] font-bold transition-all ${
                  provider === "google"
                    ? "border-white bg-white text-gray-900 hover:bg-gray-100"
                    : "border-white/30 bg-black text-white hover:border-white/60"
                } disabled:cursor-not-allowed disabled:opacity-45`}
                data-testid={`login-${provider}`}
                data-provider-available={isAvailable ? "true" : "false"}
                title={isAvailable ? detail.label : `${detail.label} is awaiting provider configuration`}
              >
                <ProviderMark provider={provider} />
                {loading === provider ? "Redirecting..." : detail.label}
                {!isAvailable && (
                  <span className="absolute right-4 h-2.5 w-2.5 rounded-full bg-amber-400" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>

        {providers.email === true && (
          <>
            <div className="my-5 flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">or use email</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <form onSubmit={requestEmailLink} className="space-y-3" data-testid="email-login-form">
              <label htmlFor="email-login" className="sr-only">Email address</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  id="email-login"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Enter any email address"
                  className="h-14 w-full rounded-2xl border border-white/15 bg-black/25 pl-12 pr-4 text-base text-white outline-none transition placeholder:text-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25"
                  disabled={Boolean(loading)}
                  data-testid="email-login-input"
                />
              </div>
              <button
                type="submit"
                disabled={Boolean(loading) || !email.trim()}
                className="flex h-14 w-full items-center justify-center rounded-2xl bg-blue-600 text-[16px] font-bold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-45"
                data-testid="email-login-submit"
              >
                {loading === "email" ? "Sending secure link..." : "Continue with Email"}
              </button>
              {emailMessage && (
                <p className="rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-center text-sm text-emerald-200" role="status">
                  {emailMessage}
                </p>
              )}
              {emailError && (
                <p className="rounded-xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-center text-sm text-red-200" role="alert">
                  {emailError}
                </p>
              )}
            </form>
          </>
        )}

        <div className="my-5 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">or use social</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="grid grid-cols-3 gap-3" aria-label="Social media sign-in">
          {SOCIAL_PROVIDERS.map((provider) => {
            const detail = PROVIDER_DETAILS[provider];
            const isAvailable = providers[provider] === true;
            return (
              <button
                key={provider}
                type="button"
                onClick={() => startLogin(provider)}
                disabled={Boolean(loading) || !isAvailable}
                aria-label={detail.label}
                title={isAvailable ? detail.label : `${detail.label} is awaiting provider configuration`}
                className={`relative flex h-14 items-center justify-center rounded-2xl shadow-lg transition hover:-translate-y-0.5 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45 ${detail.className}`}
                data-testid={`login-${provider}`}
                data-provider-available={isAvailable ? "true" : "false"}
              >
                <ProviderMark provider={provider} />
                {loading === provider && <span className="sr-only">Redirecting...</span>}
                {!isAvailable && (
                  <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full border border-black/30 bg-amber-300" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>

        {unavailableProviders.length > 0 && (
          <p className="mt-4 text-center text-[11px] leading-relaxed text-gray-500">
            Amber dots mean setup is still required for {unavailableProviders.join(", ")}.
          </p>
        )}

        <p className="text-gray-500 text-xs leading-relaxed text-center mt-7">
          By continuing you agree to our{" "}
          <a href="/terms" className="font-semibold text-blue-500 hover:text-blue-400 hover:underline">Terms</a>
          {" "}&amp;{" "}
          <a href="/privacy-policy" className="font-semibold text-blue-500 hover:text-blue-400 hover:underline">Privacy Policy</a>.
          <br />
          <a href="/account-deletion" className="font-semibold text-blue-500 hover:text-blue-400 hover:underline">
            Account and data deletion
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
